import React, { useCallback, useEffect, useReducer, useState } from "react"
import {
    criarSubconta,
    obterDadosBancariosSubconta,
    salvarDadosBancarios,
} from "../../../services/dadosBancariosSubconta"
import useLoadingStore from "../../../stores/LoadingStore"
import { Loading } from "../../RegistradorDePedidos"
import { DadosBancariosSubcontaInicial } from "../models/dadosBancariosSubcontaModel"
import { DadosBancariosSubcontaActionsTypes } from "./dadosBancariosSubcontaActions"
import { DadosBancariosSubcontaContext } from "./DadosBancariosSubcontaContext"
import { dadosBancariosSubcontaReducer } from "./dadosBancariosSubcontaReducer"

export function DadosBancariosSubcontaContextProvider({ children }: { children: React.ReactNode }) {
    const [dados, dispatch] = useReducer(
        dadosBancariosSubcontaReducer,
        DadosBancariosSubcontaInicial
    )
    const [erroCarregamento, setErroCarregamento] = useState<string | null>(null)
    const setLoading = useLoadingStore(s => s.setLoading)

    const carregar = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        try {
            setLoading(true)
            const response = await obterDadosBancariosSubconta(autorizacao)
            if ("retornoErro" in response) {
                setErroCarregamento(response.mensagem || "Erro ao carregar dados bancários")
            } else {
                setErroCarregamento(null)
                dispatch({ type: DadosBancariosSubcontaActionsTypes.CARREGAR, payload: response })
            }
        } catch {
            setErroCarregamento("Erro ao carregar dados bancários")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        carregar()
    }, [])

    const criar = useCallback(async (nomeEstabelecimento: string) => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        try {
            setLoading(true)
            const response = await criarSubconta(autorizacao, nomeEstabelecimento)
            if ("retornoErro" in response) {
                throw new Error(response.mensagem)
            }
            dispatch({ type: DadosBancariosSubcontaActionsTypes.TOKENS_CRIADOS })
            await carregar()
        } finally {
            setLoading(false)
        }
    }, [])

    const salvar = useCallback(async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        try {
            setLoading(true)
            const payload = {
                dataPersonType: dados.dataPersonType,
                nomeEmpresa: dados.nomeEmpresa,
                telefone: dados.telefone,
                endereco: dados.endereco,
                estado: dados.estado,
                cidade: dados.cidade,
                cep: dados.cep,
                responsavelName: dados.responsavelName,
                responsavelCpf: dados.responsavelCpf,
                cpfCnpj: dados.cpfCnpj,
                nomeBanco: dados.nomeBanco,
                agencia: dados.agencia,
                numeroConta: dados.numeroConta,
                tipoConta: dados.tipoConta,
                operacao: dados.operacao,
            }
            const response = await salvarDadosBancarios(autorizacao, payload)
            if ("retornoErro" in response) {
                throw new Error(response.mensagem)
            }
            dispatch({
                type: DadosBancariosSubcontaActionsTypes.ATUALIZAR_CAMPO,
                payload: {
                    campo: "possuiDadosBancariosCompletos",
                    valor: response.possuiDadosBancariosCompletos,
                },
            })
        } finally {
            setLoading(false)
        }
    }, [dados])

    return (
        <DadosBancariosSubcontaContext.Provider
            value={{ dados, dispatch, erroCarregamento, salvar, criar }}
        >
            <Loading />
            {children}
        </DadosBancariosSubcontaContext.Provider>
    )
}
